import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Card } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import moment from "moment/moment";
import { useWebSocket } from "context/WebSocketContext";

const ClusteredGatewayMarker = ({ devices = [], deviceType, selectedRow, onSelectRow, resetSelectedeDevice }) => {
    const [markers, setMarkers] = useState({});
    const [selectedTreeKey, setSelectedTreeKey] = useState(null);
    const zoomRef = useRef(null);
    const map = useMap();
    const clustererRef = useRef(null);
    const [deviceData, setDeviceData] = useState([]);
    const { linkedDevice } = useWebSocket();
    const prevAssetTrackerData = useRef(null);

    // Initialize map and clusterer
    useEffect(() => {
        setDeviceData([])
        if (!map) return;
        // clustererRef.current = null
        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({ map, algorithm: new SuperClusterAlgorithm({ maxZoom: 20 }) });
        }
        let deviceMap = new Set();
        if (devices.length >= 1) {
            setSelectedTreeKey(null);
            onSelectRow(null);
        }
        if (devices.length > 0) {
            // setSelectedTreeKey(null)
            const bounds = new google.maps.LatLngBounds();
            devices.forEach((device) => {
                const { lat, lng } = device;
                if (
                    typeof lat === 'number' && typeof lng === 'number' &&
                    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
                ) {
                    bounds.extend({ lat, lng });
                } else {
                    console.warn('Invalid coordinates:', device);
                }
            });
            setTimeout(() => {
                map.fitBounds(bounds);
            }, 200); // adjust delay as needed
            let newDevice = MapDevice(devices, deviceMap)
            // let linkedDeviceList = devices.filter(e => e.has_linked_device === true).map(f => f.linked_devices)
            // linkedDeviceList = linkedDeviceList.flat()
            // let linkedDevices = LinkedDevice(linkedDeviceList, deviceMap)
            // let device_linked = [...newDevice, ...linkedDevices]
            setDeviceData(newDevice)
        }
        // setMarkers({})

    }, [map, devices]);

    // Handle WebSocket updates
    useEffect(() => {
        if (!linkedDevice || !map || !clustererRef.current) return;
        if (prevAssetTrackerData.current === linkedDevice) return;
        prevAssetTrackerData.current = linkedDevice;

        const { device_id, payload } = linkedDevice;
        if (!device_id || !payload) return;

        const { position_latitude, position_longitude, moment_status, position_direction } = payload.linked_devices.device_value;
        const lat = parseFloat(position_latitude);
        const lng = parseFloat(position_longitude);

        if (isNaN(lat) || isNaN(lng)) return;

        const deviceId = payload.linked_devices.device_id;
        const newAnimate = moment_status === "Moving";
        const newHeading = position_direction || 0;

        setDeviceData((prev) => {
            // prev may be an array of devices, so we map and update the correct one
            if (!Array.isArray(prev)) return prev;
            return prev.map(device => {
                if (device.id === device_id) {
                    return {
                        ...device,
                        position: { lat, lng },
                        animate: newAnimate,
                        heading: newHeading
                    };
                }
                return device;
            });
        });

        // Update specific marker's position directly if it exists
        if (markers[deviceId]) {
            const marker = markers[deviceId];
            const needsClusterUpdate =
                marker.position.lat !== lat ||
                marker.position.lng !== lng;

            if (needsClusterUpdate) {
                clustererRef.current.removeMarker(marker);
            }
        }
    }, [linkedDevice, map]);

    // Update clusterer when markers change
    useEffect(() => {
        if (!clustererRef.current || !map) return;

        // Clear any existing markers first
        clustererRef.current.clearMarkers();
        const markerValues = Object.values(markers).filter(Boolean);
        // Keep only markers that are actual Google Maps marker objects
        const validMarkers = markerValues.filter(m => m && typeof m.setMap === 'function');
        if (validMarkers.length > 0) {
            clustererRef.current.addMarkers(validMarkers);
        }
    }, [markers, map]);

    // Handle selected device
    const selectedDevice = useMemo(
        () => devices.find((t) => t.id === selectedTreeKey) || null,
        [devices, selectedTreeKey]
    );
    // Center map on selected device
    useEffect(() => {
        if (selectedDevice && map) {
            const zoomLevel = map.getZoom();

            const mainPos = {
                lat: selectedDevice.lat,
                lng: selectedDevice.lng,
            };

            zoomRef.current = zoomLevel;
            // if (
            //     selectedDevice.has_linked_device &&
            //     selectedDevice.linked_devices?.length
            // ) {

            //     const bounds = new window.google.maps.LatLngBounds();
            //     bounds.extend(mainPos);
            //     selectedDevice.linked_devices.forEach(ld => {
            //         bounds.extend({
            //             lat: ld?.device_value?.position_latitude ?? ld?.entity_address_item?.latitude,
            //             lng: ld?.device_value?.position_longitude ?? ld?.entity_address_item?.longitude,
            //         });
            //     });


            //     map.fitBounds(bounds, 100); // 100px padding
            // } else {
            //     // map.setCenter(mainPos);
            //     // map.setZoom(18);
            // }
            map.setCenter(mainPos);
            map.setZoom(18);
        }
    }, [selectedDevice, map]);

    useEffect(() => {
        if (deviceType) {
            zoomRef.current = null;
            setSelectedTreeKey(null)
            map.setZoom(12);
        }
    }, [deviceType]);

    const setMarkerRef = useCallback((marker, key) => {
        setMarkers((prevMarkers) => {
            if (!marker) {
                const { [key]: _, ...newMarkers } = prevMarkers;
                return newMarkers;
            }
            return { ...prevMarkers, [key]: marker };
        });
    }, []);

    const handleInfoWindowClose = useCallback(() => {
        resetSelectedeDevice()
        setSelectedTreeKey(null);
        onSelectRow(null);
        if (zoomRef.current && map) {
            map.setZoom(zoomRef.current);
        }
    }, [map, selectedRow]);

    const handleMarkerClick = useCallback((tree) => {
        setSelectedTreeKey(tree.id);
    }, []);

    useEffect(() => {
        if (selectedRow) {
            handleMarkerClick(selectedRow)
        }
    }, [selectedRow])

    const formatAddress = (address) =>
        [
            address?.street_address1,
            address?.street_address2,
            address?.city,
            address?.state,
            address?.postal_code,
            address?.country,
        ]
            .filter(Boolean)
            .join(", ");

    return (
        <>
            {map && deviceData.map(({ id, device, position }) => (
                <AdvancedMarkerWithRefLM
                    key={id}
                    device={device}
                    position={position}
                    onClick={handleMarkerClick}
                    setMarkerRef={setMarkerRef}
                />
            ))}
            {/* Linked devices + animated lines */}
            {/* {selectedDevice?.linked_devices?.map((ld, i) => {
                const linkedPos = {
                    lat: ld?.device_value?.position_latitude ?? ld?.entity_address_item?.latitude,
                    lng: ld?.device_value?.position_longitude ?? ld?.entity_address_item?.longitude,
                };

                const RenderIcon =
                    () => {
                        return (
                            <div className="icon-wrapper" style={{ color: "#fff" }} title={ld.device_name ?? ""}>
                                <h6
                                    style={{
                                        position: "absolute",
                                        top: "-17px",
                                        color: "#000",
                                        whiteSpace: "nowrap",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {ld.device_name || ""}
                                </h6>
                                <i
                                    className={ld.entity_address_item.class_name || "fas fa-map-pin"}
                                    style={{ fontSize: "24px" }}
                                ></i>
                            </div>
                        );
                    }

                return (
                    <React.Fragment key={i}>
                        <AdvancedMarker position={linkedPos} >
                            <RenderIcon />
                        </AdvancedMarker>
                    </React.Fragment>
                );
            })} */}
            {selectedDevice && !selectedRow?.click_type && (
                <Card
                    sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                        padding: 2,
                        backgroundColor: "white",
                    }}
                >
                    <MDTypography sx={{ fontSize: "20px" }}>
                        {selectedDevice.attributes.device_data.name} #{selectedDevice.attributes.device_id}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "15px" }}>
                        Address: {formatAddress(selectedDevice.attributes?.entity_address_item)}
                    </MDTypography>
                    <CardView deviceValue={selectedDevice.attributes.device_value} />
                    <MDButton color="dark" onClick={() => handleInfoWindowClose()}>
                        Close
                    </MDButton>
                </Card>
            )}
        </>
    );
};

const AdvancedMarkerWithRefLM = ({ device, position, onClick, setMarkerRef }) => {
    const handleClick = useCallback(() => onClick(device), [onClick, device]);
    const ref = useCallback(
        (marker) => {
            setMarkerRef(marker, device.id);
        },
        [setMarkerRef, device.id]
    );

    // Validate position
    if (!position || typeof position.lat !== "number" || typeof position.lng !== "number") {
        console.warn(`Invalid position for device ${device.id}:`, position);
        return null;
    }

    const RenderIcon =
        () => {
            const iconStyle = {
                color: device.color || "#000",
            };

            return (
                <div className="icon-wrapper" style={iconStyle} title={device.title ?? ""}>
                    <h6
                        style={{
                            position: "absolute",
                            top: "-17px",
                            color: "#000",
                            whiteSpace: "nowrap",
                            fontSize: "12px",
                            fontWeight: "bold",
                        }}
                    >
                        {device.text || ""}
                    </h6>
                    <i
                        className={device.iconClassName || "fas fa-map-pin"}
                        style={{ fontSize: "24px" }}
                    ></i>
                </div>
            );
        }

    return (
        <AdvancedMarker position={position} ref={ref} onClick={handleClick}>
            <RenderIcon />
        </AdvancedMarker>
    );
};

function CardView({ deviceValue }) {

    const channels = Object.keys(deviceValue?.channels)
    const percent = channels.map(e => deviceValue?.channels[e].percent_level).join(', ');
    const fluid_height = channels.map(e => deviceValue?.channels[e].actual_fluid_height).join(', ');
    const calibrated_level = channels.map(e => deviceValue?.channels[e].calibrated_level).join(', ');
    const usable_height = channels.map(e => deviceValue?.channels[e].usable_height).join(', ');
    const volume_level = channels.map(e => deviceValue?.channels[e].volume_level).join(', ');
    const raw_level = channels.map(e => deviceValue?.channels[e].raw_level).join(', ');

    return (
        <>
            <MDTypography sx={{ fontSize: "15px" }}>
                Percent: {percent}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Volume: {volume_level}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Fluid Height: {fluid_height}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Calibrated Level: {calibrated_level}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Usable Height: {usable_height}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Raw Level: {raw_level}
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px" }}>
                Last Updated on: {moment(deviceValue?.last_updated).fromNow()}
            </MDTypography>
        </>
    )
}

function LinkedCardView({ data }) {
    return (
        data.device_type_id === 8 ?
            <>
                <MDTypography sx={{ fontSize: "20px" }}>
                    {data?.device_id} / {data?.device_name}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Device Status: {data?.moment_status}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Speed: {data?.location?.speed_mph} mph
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Heading: {data?.location?.heading}°
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Latitude: {data?.location?.latitude}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Longitude: {data?.location?.longitude}
                </MDTypography>
            </> :
            <>
                <MDTypography sx={{ fontSize: "20px" }}>
                    {data?.device_id} / {data?.device_name}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Pressure (PSI): {data?.pressure_psi}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Percent Level: {data?.level_percent}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Volume: {data?.volume_gallons} Gallons
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Latitude: {data?.entity_address_item?.latitude}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Longitude: {data?.entity_address_item?.longitude}
                </MDTypography>
            </>
    )
}

function MapDevice(devices = [], deviceMap) {
    return devices.map((device) => {
        let lat = parseFloat(device.lat);
        let lng = parseFloat(device.lng);

        // Validate coordinates before proceeding
        const isValidLat = typeof lat === 'number' && !isNaN(lat);
        const isValidLng = typeof lng === 'number' && !isNaN(lng);

        // If either coordinate is invalid, skip position or set default
        if (!isValidLat || !isValidLng) {
            console.warn(`Invalid lat/lng for device ID ${device.id}`, device.lat, device.lng);
            return {
                id: device.id,
                device: {
                    ...device,
                    iconClassName: device.iconClassName || "fas fa-map-pin",
                    color: device.color || "#000",
                    text: device.text || "",
                },
                position: null, // or undefined, or skip this device entirely
            };
        }

        // Make lng unique if needed
        while (deviceMap.has(lng)) {
            lng = parseFloat((lng + 0.000050).toFixed(6));
        }

        deviceMap.add(lng);

        return {
            id: device.id,
            device: {
                ...device,
                iconClassName: device.iconClassName || "fas fa-map-pin",
                color: device.color || "#000",
                text: device.text || "",
            },
            position: { lat, lng }
        };
    });
}

function LinkedDevice(devices = [], deviceMap) {
    return devices.map((device) => {
        let lat = parseFloat(device.device_value.position_latitude);
        let lng = parseFloat(device.device_value.position_longitude);

        // Validate coordinates before proceeding
        const isValidLat = typeof lat === 'number' && !isNaN(lat);
        const isValidLng = typeof lng === 'number' && !isNaN(lng);

        // If either coordinate is invalid, skip position or set default
        if (!isValidLat || !isValidLng) {
            console.warn(`Invalid lat/lng for device ID ${device.id}`, device.device_value.position_latitude, device.device_value.position_longitude);
            return {
                id: device.device_id,
                device: {
                    ...device,
                    iconClassName: device?.entity_address_item?.class_name || "fas fa-truck-container",
                    color: device?.color || "green",
                    text: device.device_data.name || "",
                },
                position: null, // or undefined, or skip this device entirely
            };
        }

        // Make lng unique if needed
        while (deviceMap.has(lng)) {
            lng = parseFloat((lng + 0.000050).toFixed(6));
        }

        deviceMap.add(lng);

        return {
            id: device.device_id,
            device: {
                ...device,
                iconClassName: device?.entity_address_item?.class_name || "fas fa-truck-container",
                color: device?.color || "green",
                text: device.device_data.name || "",
            },
            position: { lat, lng }
        };
    });
}

export default ClusteredGatewayMarker;